// Shared browser adapter: one frozen PlaceSearch Essentials query per server
// permit. No autocomplete, Details request, map or DOM extraction is used.
const loaders = new WeakMap();
const usedPermits = new WeakMap();
const errorMessage = 'A busca complementar não respondeu. Tente preencher o endereço ou confira a localização no mapa.';
const failure = () => new Error(errorMessage);
const queryText = value => typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
const validatePreparation = prepared => {
  if (prepared?.available !== true || prepared.acceptsLocation !== true || prepared.coordinatePersistenceEnabled !== false
    || prepared.model !== 'user-selected-point' || prepared.sku !== 'PlaceSearchEssentials'
    || prepared.maxQueries !== 1 || prepared.maxSelections !== 1 || prepared.maxResultCount !== 5 || prepared.apiVersion !== 'weekly'
    || typeof prepared.sessionPermit !== 'string' || prepared.sessionPermit.length < 32 || prepared.sessionPermit.length > 8000
    || !/^[A-Za-z0-9_-]{20,200}$/.test(prepared.browserApiKey || '')
    || !Number.isFinite(Date.parse(prepared.expiresAt)) || Date.parse(prepared.expiresAt) <= Date.now()
    || queryText(prepared.query).length < 3 || queryText(prepared.query).length > 120
    || queryText(prepared.searchQuery).length < 3 || queryText(prepared.searchQuery).length > 300) throw failure();
};
const resolveRuntime = runtime => {
  const chosen = runtime || { window: typeof window === 'undefined' ? null : window,
    document: typeof document === 'undefined' ? null : document };
  if (!chosen.window?.customElements || !chosen.document?.createElement) throw failure();
  return chosen;
};
export const loadGooglePlacesUiKit = (prepared, runtime = undefined) => {
  validatePreparation(prepared);
  const { window: win, document: doc } = resolveRuntime(runtime);
  const previous = loaders.get(win);
  if (previous) {
    if (previous.key !== prepared.browserApiKey) return Promise.reject(failure());
    return previous.promise;
  }
  if (win.google?.maps?.importLibrary) return Promise.reject(failure());
  const promise = new Promise((resolve, reject) => {
    const callback = `__brutosUiKitReady_${Date.now().toString(36)}`;
    const script = doc.createElement('script');
    const finish = () => { clearTimeout(timer); delete win[callback]; script.onerror = null; };
    const timer = setTimeout(() => { finish(); script.remove(); reject(failure()); }, 20000);
    win[callback] = async () => {
      try {
        const library = await win.google?.maps?.importLibrary?.('places');
        if (typeof library?.PlaceSearchElement !== 'function') throw failure();
        finish(); resolve(library);
      } catch { finish(); script.remove(); reject(failure()); }
    };
    const url = new URL('https://maps.googleapis.com/maps/api/js');
    url.searchParams.set('key', prepared.browserApiKey); url.searchParams.set('v', prepared.apiVersion);
    url.searchParams.set('libraries', 'places'); url.searchParams.set('loading', 'async'); url.searchParams.set('callback', callback);
    script.src = url.toString(); script.async = true; script.defer = true; script.referrerPolicy = 'strict-origin-when-cross-origin';
    script.onerror = () => { finish(); script.remove(); reject(failure()); }; doc.head.appendChild(script);
  });
  loaders.set(win, { key: prepared.browserApiKey, promise });
  return promise;
};
const readPosition = value => {
  if (!value || typeof value !== 'object') return null;
  const lat = typeof value.lat === 'function' ? value.lat() : value.lat;
  const lng = typeof value.lng === 'function' ? value.lng() : value.lng;
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)
    && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && (lat !== 0 || lng !== 0) ? { lat, lng } : null;
};
const readViewport = value => {
  if (!value || typeof value !== 'object') return null;
  const raw = typeof value.toJSON === 'function' ? value.toJSON() : value;
  if (![raw.north, raw.south, raw.east, raw.west].every(item => typeof item === 'number' && Number.isFinite(item))
    || Math.abs(raw.north) > 90 || Math.abs(raw.south) > 90 || Math.abs(raw.east) > 180 || Math.abs(raw.west) > 180
    || raw.south > raw.north) return null;
  return { north: raw.north, south: raw.south, east: raw.east, west: raw.west };
};
const samePoint = (left, right) => ['lat', 'lng'].every(field => left[field].toFixed(7) === right[field].toFixed(7));
export const createGooglePlacesUiKitSession = async ({ prepared, container, onSuggestion, onError = undefined,
  runtime = undefined, signal = undefined, locationBias = undefined, locationRestriction = undefined }) => {
  validatePreparation(prepared);
  const frozen = { permit: prepared.sessionPermit, expiresAt: Date.parse(prepared.expiresAt), query: prepared.query,
    searchQuery: prepared.searchQuery, maxResultCount: 5 };
  const env = resolveRuntime(runtime);
  const permits = usedPermits.get(env.window) || new Map();
  for (const [token, expiry] of permits) if (expiry <= Date.now()) permits.delete(token);
  if (permits.has(frozen.permit) || signal?.aborted) throw failure();
  permits.set(frozen.permit, frozen.expiresAt); usedPermits.set(env.window, permits);
  const library = await loadGooglePlacesUiKit(prepared, env);
  if (signal?.aborted || frozen.expiresAt <= Date.now()) throw failure();
  const search = new library.PlaceSearchElement();
  search.selectable = true;
  // Google's default vertical layout, logo and legal disclosure remain intact.
  const request = env.document.createElement('gmp-place-text-search-request');
  request.maxResultCount = frozen.maxResultCount;
  // Text Search accepts one search area. Prefer the server's bounded region;
  // a bias is used only when no valid restriction is available.
  const restriction = readViewport(locationRestriction);
  const bias = restriction ? null : readPosition(locationBias);
  if (restriction) request.locationRestriction = restriction;
  else if (bias) request.locationBias = bias;
  const content = env.document.createElement('gmp-place-content-config');
  content.appendChild(env.document.createElement('gmp-place-address'));
  search.appendChild(request); search.appendChild(content);
  let delivered = false; let disposed = false;
  const report = () => { if (!disposed) onError?.(errorMessage); };
  const select = event => {
    if (disposed || delivered || signal?.aborted || frozen.expiresAt <= Date.now()) return;
    try {
      const place = event?.place; const point = readPosition(place?.location);
      const id = typeof place?.id === 'string' ? place.id.trim() : '';
      if (!id || id.length > 512 || !point) { report(); return; }
      const rendered = Array.isArray(search.places) ? search.places : [];
      const current = rendered.find(item => item?.id === id && readPosition(item?.location) && samePoint(readPosition(item.location), point));
      if (!current) { report(); return; }
      delivered = true; search.selectable = false;
      // The server independently qualifies the selected position. This callback
      // is neither a Google entity attestation nor permission to persist it.
      onSuggestion({ placeId: id, position: point, viewport: readViewport(place.viewport) });
    } catch { report(); }
  };
  const dispose = () => {
    if (disposed) return; disposed = true;
    search.removeEventListener('gmp-select', select); search.removeEventListener('gmp-error', report);
    signal?.removeEventListener('abort', dispose); search.remove();
  };
  search.addEventListener('gmp-select', select); search.addEventListener('gmp-error', report);
  signal?.addEventListener('abort', dispose, { once: true });
  container.appendChild(search);
  // This is the single billing trigger; no later input changes can mutate it.
  request.textQuery = frozen.searchQuery;
  return { dispose };
};
